import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { useClient } from '../../../hooks/useClient';
import { Reservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { Organization } from '../../../types/organization';
import { ExternalUserWithCoupons } from '../../../types/externalUser';
import { Coupon, UserCouponWithCouponAndUsesCount } from '../../../types/coupon';
import { getRequestUrl } from '../../../utils/urlUtils';
import { ReservationForm } from '../validators';

interface Options {
  organization: Organization | undefined;
  reservationType: ReservationType | undefined;
  resource: Reservation | undefined;
  form: ReservationForm;
  isModifying: boolean;
}

/** Loads the coupons the coupon field can choose from and the coupon the reservation uses. */
export const useReservationCoupons = ({ organization, reservationType, resource, form, isModifying }: Options) => {
  const { get } = useClient();

  const isModifyingCoupon = reservationType?.canUseCoupon && isModifying && form.useCoupon;
  const {
    data: orgCoupons,
    isLoading: orgCouponsLoading,
    mutate: mutateOrgCoupons,
  } = useSWR(organization && isModifyingCoupon ? `/organizations/${organization.id}/coupons` : null, get<Coupon[]>);
  const {
    data: activeUserCoupons,
    isLoading: activeUserCouponsLoading,
    mutate: mutateActiveUserCoupons,
  } = useSWR(
    organization && reservationType?.canUseCoupon && form.externalUsers?.[0]?.id
      ? `/organizations/${organization.id}/external-users/${form.externalUsers?.[0]?.id}/active-coupons`
      : null,
    get<UserCouponWithCouponAndUsesCount[]>,
  );
  const { data: usersWithActiveCoupons, isLoading: usersWithActiveCouponsLoading } = useSWR(
    organization && isModifyingCoupon
      ? getRequestUrl({
          url: `/organizations/${organization.id}/external-users-with-active-coupons`,
          stringParams: {
            excludeUserId: form.externalUsers?.[0]?.id,
          },
        })
      : null,
    get<ExternalUserWithCoupons[]>,
  );
  const couponSelectionDataLoading = orgCouponsLoading || activeUserCouponsLoading || usersWithActiveCouponsLoading;

  const couponId = form?.userCouponId || resource?.userCouponId;
  const {
    data: selectedUserCoupon,
    isLoading: selectedUserCouponLoading,
    mutate: mutateSelectedUserCoupon,
  } = useSWR(
    organization && reservationType?.canUseCoupon && couponId
      ? `/organizations/${organization.id}/user-coupons/${couponId}`
      : null,
    get<UserCouponWithCouponAndUsesCount>,
  );

  const selectedCouponHasUses = !!selectedUserCoupon?.usesRemaining && selectedUserCoupon?.usesRemaining > 0;
  const couponWasPurchased = !!resource?.userCouponId && !!resource.price && resource.price > 0;
  // The coupon of the saved reservation. It stays while the panel is open, also after the form selects another coupon.
  const reservationUserCoupon = useRef<UserCouponWithCouponAndUsesCount>();
  useEffect(() => {
    if (resource?.userCouponId && selectedUserCoupon && selectedUserCoupon.id === resource?.userCouponId) {
      reservationUserCoupon.current = selectedUserCoupon;
    }
  }, [resource?.userCouponId, selectedUserCoupon]);

  return {
    orgCoupons,
    orgCouponsLoading,
    mutateOrgCoupons,
    activeUserCoupons,
    activeUserCouponsLoading,
    mutateActiveUserCoupons,
    usersWithActiveCoupons,
    usersWithActiveCouponsLoading,
    couponSelectionDataLoading,
    selectedUserCoupon,
    selectedUserCouponLoading,
    mutateSelectedUserCoupon,
    selectedCouponHasUses,
    couponWasPurchased,
    reservationUserCoupon,
  };
};
